import { useLayoutEffect, useMemo, useState } from 'react';
import type { PopperProps } from '@mui/material';

export const GUIDE_CARD_WIDTH = 320;
export const GUIDE_CARD_GAP = 12;
// Clears an Autocomplete's open-popup button, which sits just outside the field.
export const GUIDE_FIELD_GAP = 36;

export type GuideCardPlacement =
  | { kind: 'beside'; side: 'right' | 'left'; frame: HTMLElement }
  | { kind: 'field' };

/**
 * Where the step card goes so it never sits on the wizard content under the
 * field (#4185): beside the dialog when there is room, else next to the field.
 * Search results and menus open downwards, so below is the last resort.
 */
export function chooseGuideCardPlacement(anchor: HTMLElement, viewportWidth: number): GuideCardPlacement {
  const frame = anchor.closest<HTMLElement>('.MuiDialog-paper');
  if (!frame) return { kind: 'field' };
  const rect = frame.getBoundingClientRect();
  const needed = GUIDE_CARD_WIDTH + GUIDE_CARD_GAP * 2;
  if (viewportWidth - rect.right >= needed) return { kind: 'beside', side: 'right', frame };
  if (rect.left >= needed) return { kind: 'beside', side: 'left', frame };
  return { kind: 'field' };
}

/** The dialog's sides at the field's height, so the card lines up with the field. */
function besideFrame(anchor: HTMLElement, frame: HTMLElement): NonNullable<PopperProps['anchorEl']> {
  return {
    contextElement: anchor,
    getBoundingClientRect: () => {
      const field = anchor.getBoundingClientRect();
      const box = frame.getBoundingClientRect();
      return {
        x: box.left,
        y: field.top,
        top: field.top,
        bottom: field.bottom,
        left: box.left,
        right: box.right,
        width: box.width,
        height: field.height,
        toJSON: () => undefined,
      };
    },
  };
}

/** Placement plus the Popper anchor, re-chosen when the window or the dialog resizes. */
export function useGuideCardPlacement(anchor: HTMLElement | null): {
  placement: GuideCardPlacement;
  popperAnchor: PopperProps['anchorEl'];
} {
  const [layoutTick, setLayoutTick] = useState(0);

  useLayoutEffect(() => {
    const update = (): void => {
      setLayoutTick((tick) => tick + 1);
    };
    const frame = anchor?.closest<HTMLElement>('.MuiDialog-paper');
    const observed = new ResizeObserver(update);
    if (frame) observed.observe(frame);
    window.addEventListener('resize', update);
    return () => {
      observed.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [anchor]);

  return useMemo(() => {
    void layoutTick;
    if (!anchor) return { placement: { kind: 'field' }, popperAnchor: null };
    const placement = chooseGuideCardPlacement(anchor, window.innerWidth);
    return {
      placement,
      popperAnchor: placement.kind === 'beside' ? besideFrame(anchor, placement.frame) : anchor,
    };
  }, [anchor, layoutTick]);
}
